import {Component, OnInit, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {MedicationService} from '@services/medication.service';
import {SupplierService} from '@services/supplier.service';
import {ToastrService} from 'ngx-toastr';
import {FilterConfig} from '@components/search-filter/search-filter.component';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';

@Component({
    selector: 'app-medication-list',
    templateUrl: './medication-list.component.html',
    styleUrl: './medication-list.component.scss'
})
export class MedicationListComponent implements OnInit, OnDestroy {
    public data: any[] = [];
    public name: string = '';
    totalRecords: number = 0;
    pageSize: number = 10;
    currentPage: number = 1;
    public loading = true;
    public userDetail: any;
    public displayDeleteDialog = false;
    public selectedRecordId: number = 0;
    public filterForm: FormGroup;
    public medicationListFilterForm: FormGroup;
    public filteringData: any = null;

    public chickTypes: any[] = [];
    public suppliers: any[] = [];
    public cycles: any[] = [];
    public expiringSoon: number = 0;
    public outOfStock: number = 0;
    public needToBeOrdered: number = 0;

    // Subject for component destruction
    private destroy$ = new Subject<void>();

    // Configuration for the reusable search filter - will be built dynamically
    public medicationFilterConfig: FilterConfig = {
        showFarmFilter: false,
        showHouseFilter: false,
        fields: []
    };

    constructor(
        private medicationService: MedicationService,
        private supplierService: SupplierService,
        private toastr: ToastrService,
        private fb: FormBuilder
    ) {}

    ngOnInit() {
        this.userDetail = JSON.parse(localStorage.getItem('UserDetail'));
        this.initializeForms();
        this.setupFormSubscriptions();
        this.loadFilterData();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeForms() {
        this.filterForm = this.fb.group({
            brandName: [''],
            chickType: [null],
            expiryDate: [null],
            supplierId: [null]
        });

        this.medicationListFilterForm = this.fb.group({
            name: [''],
            supplierId: [0],
            expiryDate: [null]
        });
    }

    private setupFormSubscriptions() {
        // Only setup debounced filtering for brandName (text input)
        // Other filters should be applied immediately when changed by user interaction
        this.filterForm
            .get('brandName')
            ?.valueChanges.pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((brandName) => {
                // Only apply filter if there's actual content or if we're clearing
                this.applyFormFilters();
            });
    }

    private applyFormFilters() {
        this.currentPage = 1;
        const currentFilters = this.filterForm.value;

        // Check if any filter has a meaningful value
        const hasFilters = this.hasValidFilters(currentFilters);

        if (hasFilters) {
            this.filteringData = this.mapFilterParamsForAPI(currentFilters);
            this.getMedicationList(this.filteringData);
        } else {
            this.filteringData = null;
            this.getMedicationList();
        }
    }

    private hasValidFilters(filters: any): boolean {
        return !!(
            (filters.brandName && filters.brandName.trim()) ||
            filters.chickType ||
            (filters.supplierId && filters.supplierId !== 0) ||
            filters.expiryDate
        );
    }

    // Load dynamic data for filters
    private loadFilterData() {
        this.loadSuppliers();
        this.getMedicationList();
        this.loadMedicationStats();
    }

    // Load medication statistics similar to medication-panel
    private loadMedicationStats() {
        const farmId = this.filterForm.get('farmId')?.value;

        if (!farmId) {
            this.expiringSoon = 0;
            this.outOfStock = 0;
            this.needToBeOrdered = 0;
            return;
        }

        this.medicationService.GetMedicationDataNumbers(farmId).subscribe({
            next: (response) => {
                this.expiringSoon = response.expiringSoon || 0;
                this.outOfStock = response.outofStock || 0;
                this.needToBeOrdered = response.needToBeOrdered || 0;
            },
            error: () => {
                this.expiringSoon = 0;
                this.outOfStock = 0;
                this.needToBeOrdered = 0;
            }
        });
    }

    // Load suppliers from API
    private loadSuppliers() {
        this.supplierService.getSupplierList().subscribe((suppliers) => {
            this.suppliers = suppliers.map((supplier: any) => ({
                value: supplier.id,
                label: supplier.name
            }));
            this.buildFilterConfig();
        });
    }

    // Extract unique values from medication data for filters
    private extractFilterOptionsFromData(medications: any[]) {
        const uniqueChickTypes = [
            ...new Set(
                medications.map((med) => med.chickTypeName).filter(Boolean)
            )
        ];
        this.chickTypes = uniqueChickTypes.map((type) => ({
            value: type,
            label: type
        }));

        this.buildFilterConfig();
    }

    // Build filter configuration with dynamic data
    private buildFilterConfig() {
        this.medicationFilterConfig = {
            showFarmFilter: false,
            showHouseFilter: false,
            fields: [
                {
                    key: 'brandName',
                    label: 'Brand Name',
                    type: 'text',
                    placeholder: 'Enter Brand Name'
                },
                {
                    key: 'chickType',
                    label: 'Chick Type',
                    type: 'select',
                    options: this.chickTypes
                },
                {
                    key: 'expiryDate',
                    label: 'Expiry Date',
                    type: 'date'
                },
                {
                    key: 'supplierId',
                    label: 'Supplier',
                    type: 'select',
                    options: this.suppliers
                }
            ]
        };
    }

    getMedicationList(filterParams?: any) {
        this.loading = true;

        let apiParams = filterParams;
        if (filterParams) {
            // Remove null, undefined, and empty string values
            apiParams = Object.fromEntries(
                Object.entries(filterParams).filter(
                    ([_, value]) =>
                        value !== null && value !== undefined && value !== ''
                )
            );

            // If no valid filters remain, treat as no filters
            if (Object.keys(apiParams).length === 0) {
                apiParams = undefined;
            }
        }

        this.medicationService
            .getMedicationList(
                this.userDetail?.id || 0,
                this.currentPage,
                this.pageSize,
                apiParams
            )
            .subscribe(
                (result) => {
                    this.data = result?.medications || [];

                    if (this.data.length === 0) {
                        this.toastr.warning('No Records Found');
                    }

                    // Use actual data length for totalRecords - much more reliable
                    if (apiParams) {
                        // When filtering, we get all filtered results, so use that length
                        this.totalRecords = this.data.length;
                        // For filtered results, show all on one page or implement client-side pagination
                        if (this.data.length > this.pageSize) {
                            // If you want to keep server-side pagination for filtered results,
                            // you'd need to get the count from a separate API call
                            this.totalRecords = this.data.length;
                        }
                    } else {
                        // For unfiltered results, if we got a full page, there might be more
                        if (this.data.length === this.pageSize) {
                            // Estimate total based on current page - this is rough but better than wrong API count
                            this.totalRecords =
                                this.currentPage * this.pageSize + 1; // +1 to show "next" page exists
                        } else {
                            // Less than full page means this is the last page
                            this.totalRecords =
                                (this.currentPage - 1) * this.pageSize +
                                this.data.length;
                        }
                    }

                    // Only extract filter options when loading initial data (no filters)
                    if (!apiParams && this.data.length > 0) {
                        this.extractFilterOptionsFromData(this.data);
                    }

                    this.loading = false;
                },
                (error) => {
                    this.loading = false;
                    this.data = [];
                    this.totalRecords = 0;
                    this.toastr.error('Failed to load medication list');
                }
            );
    }

    // Handle filter events from the reusable search filter component
    onMedicationFilterApplied(filterValues: any) {
        // Update the form with the filter values WITHOUT triggering valueChanges
        this.filterForm.patchValue(filterValues, {emitEvent: false});
        this.currentPage = 1; // Reset to first page

        // Keep active filtering data so paginator requests stay filtered until cleared
        this.filteringData = this.mapFilterParamsForAPI(filterValues);
        this.getMedicationList(this.filteringData);
    }

    // Map filter parameters to what the API expects
    private mapFilterParamsForAPI(filterParams: any): any {
        const mappedParams: any = {};

        // Map brandName to 'Name' field
        if (filterParams.brandName && filterParams.brandName.trim()) {
            mappedParams.Name = filterParams.brandName.trim();
        }

        // Map chickType to ChickTypeName
        if (filterParams.chickType) {
            mappedParams.ChickTypeName = filterParams.chickType;
        }

        if (filterParams.expiryDate) {
            mappedParams.ExpiryDate = filterParams.expiryDate;
        }

        if (filterParams.supplierId && filterParams.supplierId !== 0) {
            mappedParams.SupplierId = filterParams.supplierId;
        }

        return mappedParams;
    }

    onMedicationFilterCleared() {
        // Reset the form WITHOUT triggering valueChanges
        this.filterForm.reset(
            {
                brandName: '',
                chickType: null,
                expiryDate: null,
                supplierId: null
            },
            {emitEvent: false}
        );

        // Reset other filter related properties
        this.name = '';
        this.currentPage = 1;
        this.filteringData = null;

        // Get unfiltered list
        this.getMedicationList();
    }

    filterMedication(): void {
        const filterValues = this.medicationListFilterForm.value;

        // Build filter data, excluding empty/null values
        const filterData: any = {};

        if (filterValues.name && filterValues.name.trim()) {
            filterData.Name = filterValues.name.trim();
        }

        if (filterValues.supplierId && filterValues.supplierId !== 0) {
            filterData.SupplierId = filterValues.supplierId;
        }

        if (filterValues.expiryDate) {
            filterData.ExpiryDate = filterValues.expiryDate;
        }

        // If no valid filters, clear filtering
        this.filteringData =
            Object.keys(filterData).length > 0 ? filterData : null;
        this.currentPage = 1;
        this.getMedicationList(this.filteringData);
    }

    onPageChange(event: any) {
        this.currentPage = event.page + 1; // PrimeNG paginator starts from 0
        this.getMedicationList(this.filteringData);
    }

    deleteMedication(medicationId: any) {
        this.selectedRecordId = medicationId;
        this.displayDeleteDialog = true;
    }

    deleteItem() {
        if (this.selectedRecordId) {
            this.medicationService
                .deleteMedication(this.selectedRecordId)
                .subscribe({
                    next: (response) => {
                        this.displayDeleteDialog = false;
                        this.selectedRecordId = 0;
                        this.toastr.success('Medication deleted successfully.');

                        // Refresh the current page with existing filters
                        this.getMedicationList(this.filteringData);
                    },
                    error: (error) => {
                        this.toastr.error('Failed to delete medication');
                        this.displayDeleteDialog = false;
                        this.selectedRecordId = 0;
                    }
                });
        }
    }

    cancelDelete() {
        this.displayDeleteDialog = false;
        this.selectedRecordId = 0;
    }
}
